import { useTranslation } from "react-i18next";
import { useState } from "react";
import { DropzoneStyled } from "@/components/styled/dropzoneStyled";
import { usePostS3UploadUrlArrayCustomCotation } from "@/api/s3.api";
import { DocumentUrlDto, PostPutPresignedUrlArrayDto } from "@/types/api/api-types";
import * as nanoid from "nanoid";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { sanitizeKey } from "@/lib/utils";

type UploadInvoiceProps = {
  handleUpload: (key: string) => void;
  disabled?: boolean;
};

export const UploadInvoice = ({ handleUpload, disabled = false }: UploadInvoiceProps) => {
  const { t } = useTranslation("boClients");
  const [files, setFiles] = useState<File[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: postS3UploadUrlArray } = usePostS3UploadUrlArrayCustomCotation();

  const createS3UploadUrlArrayPayload = () => {
    const urlsArray: PostPutPresignedUrlArrayDto = {
      urls: [],
      isPrivate: false,
    };
    files?.forEach((file: any) => {
      const fileIdTmp = nanoid.nanoid();
      urlsArray.urls.push({
        id: "",
        key: "other-invoices/" + fileIdTmp + "_" + sanitizeKey(file.name),
        isInvoice: false,
        contentType: file.type,
      });
    });
    return urlsArray;
  };

  const getUploadUrlArray = async (urlsArray: PostPutPresignedUrlArrayDto) => {
    try {
      const ret = await postS3UploadUrlArray(urlsArray);
      return ret.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const documentsKey: string[] = [];
    if (files && files.length > 0) {
      const urlsArray: PostPutPresignedUrlArrayDto = createS3UploadUrlArrayPayload();

      const signedUrlArray: DocumentUrlDto | null = await getUploadUrlArray(urlsArray);
      if (!signedUrlArray || signedUrlArray.length === 0) {
        return;
      }
      try {
        const uploadPromises: Promise<any>[] = [];
        for (const signedUrl of signedUrlArray) {
          const fileName = signedUrl.key.split("/")[1].slice(22);
          const file = files.find((otherFile) => sanitizeKey(otherFile.name) === fileName);
          if (!file) return;
          uploadPromises.push(
            fetch(signedUrl.url, {
              method: "PUT",
              body: file,
              headers: {
                "Content-Type": file.type,
              },
            }),
          );
        }
        await Promise.all(uploadPromises);
        documentsKey.push(...signedUrlArray.map((el) => el.key));
        handleUpload(documentsKey[0]);
      } catch (error) {
        console.error("Error uploading file:", error);
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRemoveFile = () => {
    setFiles(null);
  };

  const handleFileChange = (file: File[]) => {
    if (file && file[0]) {
      setFiles([file[0]]);
    }
  };

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <DropzoneStyled
        files={files}
        setFiles={setFiles}
        handleFileChange={handleFileChange}
        handleRemoveFile={handleRemoveFile}
        dropZoneText={t("invoicesUpload.dropZoneText")}
      />
      <button
        disabled={!files || files.length === 0 || isLoading || disabled}
        type="button"
        onClick={handleSubmit}
        className="bg-brand700 hover:bg-brand600 flex h-fit w-fit flex-row gap-4 self-center rounded-lg px-6 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
      >
        {isLoading && <StyledSpinner width="20" height="20" />}
        {t("invoicesUpload.submit")}
      </button>
    </div>
  );
};
